import { RoleEnum } from '../api';
import { ReactSelectOption } from '../types';
import i18next from 'i18next';
import { useApiClient } from '../hooks/useApiClient';
import { Api } from '../api'; // Adjust the import path
import { apiConfig } from '../config/api-config';
import { UserGroup } from 'src/api/models/Enums';
import _ from 'lodash';

const apiClient = new Api(apiConfig); // Manually create the API client

interface BuildUserFormValuesProps {
	role: RoleEnum;
	response: any;
	backofficeUsers: ReactSelectOption[];
	userSales: any;
	companyDetail: any;
}

export const buildUserFormValues = ({
	role,
	response,
	backofficeUsers,
	userSales,
	companyDetail,
}: BuildUserFormValuesProps): any => {
	const formValues = {
		username: response.username,
		role: response.role,
		given_name: response.given_name,
		family_name: response.family_name,
		phone: response.phone,
		phone_backup: response.phone_backup,
		email_backup: response.email_backup,
	};

	// Map reference_partner get user response into a ReactSectionOption object for the form
	const getReferencePartnerValues = (partners: string[]): ReactSelectOption[] =>
		partners.map((dealer) => ({ label: dealer, value: dealer }));

	const getUserGroups = (groups: any): ReactSelectOption[] => {
		if (_.isString(groups) && groups?.trim() === '') {
			return [];
		}

		return groups?.map((group) => ({ label: group, value: group })) ?? [];
	}

	const getReferenceBackofficeValue = (
		resReferenceBackoffice: string | null
	): ReactSelectOption | null =>
		resReferenceBackoffice
			? backofficeUsers.find((el) => el.value === resReferenceBackoffice) || {
					value: resReferenceBackoffice,
					label: resReferenceBackoffice,
			  }
			: null;

	const getSalesValues = (
		resSales: string[] | undefined
	): ReactSelectOption[] =>
		resSales
			?.map(
				(user) => userSales.find((sale) => user === sale.originalValue) || null
			)
			.filter(Boolean) || [];

	const getAreaBackofficeValues = (
		mails: string[] = []
	): ReactSelectOption[] => {
		const loweredMails = (mails || [])
			.filter(Boolean)
			.map((mail) => mail.toLowerCase());
		const options = (backofficeUsers || []).filter((user) =>
			loweredMails.includes(user?.value?.toLowerCase())
		);

		return mails.length && !options.length
			? mails.map((item) => ({ label: item, value: item }))
			: options;
	};

	// TODO map or api call to sf accounts to map sf account in reagione sciale

	const getTipologiaValues = (
		resCompanyDetails: string
	): ReactSelectOption[] => {
		const test = resCompanyDetails
			? companyDetail.find((el) => el.vale === resCompanyDetails) || {
					value: resCompanyDetails,
					label: resCompanyDetails,
			  }
			: null;
		return test;
	};

	if (RoleEnum.SEGNALATORE === response.role) {
		const ref_sales_dealer = response.reference_sales_dealer;
		Object.assign(formValues, {
			codice_fiscale: response.codice_fiscale,
			partita_iva: response.partita_iva,
			reference_sales_dealer: ref_sales_dealer ? {value: ref_sales_dealer, label: ref_sales_dealer} : "",
			provvigioni_segnalatore: response.provvigioni_segnalatore,
			provvigioni_dealer: response.provvigioni_dealer || (100 - parseFloat(response.provvigioni_segnalatore)),
			documents: response.documents,
			reference_partner: response.reference_partner?.map((item) => ({
				value: item,
				label: item,
			})),
			iban: response.iban,
			business_name: response.business_name,
			reference_backoffice: getReferenceBackofficeValue(response.reference_backoffice) || '',
		});
	}

	// assign form values according to the user role selected in the form
	switch (role) {
		case RoleEnum.MASTER:
		case RoleEnum.BACKOFFICE:
		case RoleEnum.AREA_MANAGER:
			return {
				...formValues,
				reference_partner:
					getReferencePartnerValues(response.reference_partner) || [],
				can_reassign_leads: response.can_reassign_leads || false,
				area_backoffices:
					role === RoleEnum.AREA_MANAGER
						? getAreaBackofficeValues(response.area_backoffices)
						: undefined,
				group: getUserGroups(response.group),
			};

		case RoleEnum.SALES:
		case RoleEnum.SALES_DEALER:
		case RoleEnum.SALES_INTERNAL:
			return {
				...formValues,
				reference_partner:
					getReferencePartnerValues(response.reference_partner) || [],
				reference_backoffice:
					getReferenceBackofficeValue(response.reference_backoffice) || '',
				visibility_tag: response.visibility_tag || [],
				is_active: response.is_active || false,
				is_salesforce_sales: response.is_salesforce_sales || false,
				display_commissions: response.display_commissions || false,
			};

		case RoleEnum.FLEET_MANAGER:
			return {
				...formValues,
				sales: getSalesValues(response.sales),
				is_active_email: response.is_active_email || false,
				is_active: response.is_active || false,
				company_detail: getTipologiaValues(response.company_detail) || [],
				// business_name: getBusinessNameValue(response.business_name) || '',
				business_name: response.business_name || '',
			};

		default:
			return formValues;
	}
};

export const userFormValidationFn = (values, currentUserRole) => {
	const errors: any = {};
	const REQUIRED_MESSAGE = i18next.t('mandatory');
	const UNSUPPORTED = i18next.t('not_supported');

	if (!/^[A-Z0-9._%+\-']+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.username)) {
			errors.username = i18next.t('username_must_be_a_valid_email_address');
	}
	if (!values?.username) {
		errors.username = REQUIRED_MESSAGE;
	}

	if (!values?.role) {
		errors.role = REQUIRED_MESSAGE;
	}

	if (!values?.given_name) {
		errors.given_name = REQUIRED_MESSAGE;
	}

	if (!values?.family_name) {
		errors.family_name = REQUIRED_MESSAGE;
	}

	if (values?.role === RoleEnum.FLEET_MANAGER && !values?.business_name) {
		errors.business_name = REQUIRED_MESSAGE;
	}

	if ([RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL].includes(values?.role) && !values.reference_backoffice) {
		errors.reference_backoffice = REQUIRED_MESSAGE;
	}

	if (currentUserRole !== RoleEnum.SALES_DEALER && values?.role === RoleEnum.SEGNALATORE && !values.reference_backoffice) {
		errors.reference_backoffice = REQUIRED_MESSAGE;
	}

	if (values?.role === RoleEnum.SEGNALATORE) {
		if (!values.provvigioni_segnalatore) {
			errors.provvigioni_segnalatore = REQUIRED_MESSAGE;
		}

		if (!values.phone) {
			errors.phone = REQUIRED_MESSAGE;
		}

		if (!values.codice_fiscale && !values.partita_iva) {
			errors.partita_iva = REQUIRED_MESSAGE;
			errors.codice_fiscale = REQUIRED_MESSAGE;
		}

		if (!values.reference_sales_dealer) {
			errors.reference_sales_dealer = REQUIRED_MESSAGE;
		}
	}

	if (
		!values?.reference_partner?.length &&
		[
			RoleEnum.MASTER,
			RoleEnum.BACKOFFICE,
			RoleEnum.SALES,
			RoleEnum.SALES_DEALER,
			RoleEnum.SALES_INTERNAL,
			RoleEnum.AREA_MANAGER,
			RoleEnum.SEGNALATORE,
		].includes(values?.role)
	) {
		errors.reference_partner = REQUIRED_MESSAGE;
	}

	if (
		!values?.reference_partner?.length &&
		[RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL].includes(
			values?.role
		)
	) {
		errors.reference_partner = REQUIRED_MESSAGE;
	}

	if (
		values?.reference_partner?.length &&
		![
			RoleEnum.MASTER,
			RoleEnum.BACKOFFICE,
			RoleEnum.SALES,
			RoleEnum.SALES_DEALER,
			RoleEnum.SALES_INTERNAL,
			RoleEnum.SEGNALATORE,
			RoleEnum.AREA_MANAGER,
		].includes(values?.role)
	) {
		errors.reference_partner = UNSUPPORTED;
	}

	if (
		values?.business_name?.length &&
		![RoleEnum.FLEET_MANAGER, RoleEnum.SEGNALATORE].includes(values?.role)
	) {
		errors.business_name = REQUIRED_MESSAGE;
	}

	return errors;
};

export const userGroups = [
	{
		value: UserGroup.CAN_APPROVE_SEGNALATORI,
		label: UserGroup.CAN_APPROVE_SEGNALATORI,
	},
]
